/**
 * feat-270 — every sentence the curriculum-approval surface says.
 *
 * WHY A CONSTANTS FILE, in `autoSaveCopy.ts`'s own words: *"FE vocabulary in this
 * app renders through constants precisely so a literal-text grep cannot return a
 * TRUE ZERO for copy that is visibly on screen."* Search here, not the JSX.
 *
 * Design authority: `design/approval-queue.html` (11 states),
 * `design/editor-integration.html` (9 panels) and `design/NOTES.md` §8, which is
 * where the reasoning behind the load-bearing strings lives. Four of them are
 * decisions rather than wording, and each is marked below so a well-meaning
 * copy-edit has to argue with the reason rather than just the sentence:
 *
 *  1. **"On phones now" / "Draft", never "Before" / "After".** History's
 *     Before/After is a TEMPORAL claim: what it was, what it is now. Approval is
 *     a SIMULTANEOUS one: what the learner is reading right now, beside what the
 *     editor is looking at right now. Before/After here would teach the reader
 *     that the change has already taken effect — the exact misunderstanding this
 *     whole feature exists to prevent.
 *  2. **"· differs", not "· changed".** A state word about two columns, not an
 *     event word about an edit. The diff is cumulative (AT-26): after three
 *     edits it shows approved → current, not edit 3, so "changed" invites the
 *     reader to picture one edit that may have been three by three people.
 *  3. **"Last edited by", never a bare name** (AT-26c). History supplies only the
 *     LATEST editor while a diff can span several edits by several people; a bare
 *     name beside a two-field diff falsely attributes all of it to one person, in
 *     precisely the delegated-authoring scenario this feature exists for.
 *  4. **Name the consequence, not the status.** "Never approved" is our word;
 *     "employees can't see it yet" is the reader's. Every state line leads with
 *     the consequence and mentions the status second, if at all.
 *
 * Every level word is a `QUIZ_TERMS` lookup — `quizTerminology.ts`'s header
 * forbids hard-coding "Lesson"/"Drill"/"Question" anywhere in the editor. The
 * mockups write them in `{braces}` so the seams are visible; here they are real
 * lookups, which is why so much of this file is functions rather than strings.
 */

import { QUIZ_TERMS, termCount } from '../quizTerminology';

const LESSON = QUIZ_TERMS.lesson.singular;
const LESSONS = QUIZ_TERMS.lesson.plural;

/**
 * "3 changes" / "1 change". Changes are not a curriculum level, so this is not a
 * `termCount` key — but it is pluralized in one place for the same reason.
 */
export function changeCount(n: number): string {
  return `${n} ${n === 1 ? 'change' : 'changes'}`;
}

/** "3 items" / "1 item" — the confirm dialog's per-lesson right-hand count. */
export function itemCount(n: number): string {
  return `${n} ${n === 1 ? 'item' : 'items'}`;
}

export const APPROVAL_QUEUE_COPY = {
  // ── page shell ────────────────────────────────────────────────────────────
  title: 'Approvals',
  /** The tenant name is rendered as its own emphasized element, so it is a slot. */
  subtitlePrefix: "Changes waiting to go to employees' phones at ",
  subtitleSuffix: '.',
  /** Ruling 4: the consequence, before any mechanism. */
  subtitleConsequence: `Until you approve a ${LESSON}, employees keep seeing the version you approved last.`,
  backToEditor: '← Edit content',

  // ── list header ───────────────────────────────────────────────────────────
  selectAll: (n: number) => `Select all ${n}`,
  waitingCount: (lessons: number, changes: number) =>
    `${termCount(lessons, 'lesson')} waiting · ${changeCount(changes)}`,
  refresh: '↻ Refresh',

  // ── lesson card ───────────────────────────────────────────────────────────
  approve: 'Approve',
  expand: (title: string) => `Show what changed in ${title}`,
  collapse: (title: string) => `Hide what changed in ${title}`,
  /**
   * The pending chip is `● N` and NEUTRAL. The sentence lives in `title` +
   * `aria-label`, never in the chip — NOTES §3a measured the full label at
   * 60.08px against a 157.09px title budget in the tree, and the queue keeps the
   * words only because a 1000px card has room a 280px pane does not.
   */
  pendingChipLabel: (n: number) =>
    `${changeCount(n)} waiting for approval — employees still see the approved version`,
  /** The ONE state that earns colour (`attention-*`). NOTES §3. */
  neverApprovedChip: 'Not live',
  neverApprovedChipLabel: `Never approved — employees cannot see this ${LESSON} at all yet`,
  changedSinceOpenedChip: 'Changed since you opened this',
  lastChangeBy: (who: string, when: string) => `Last change by ${who} · ${when}`,
  createdBy: (who: string, when: string) => `Created by ${who} · ${when}`,

  /** Ruling 4 again: what the reader cannot do, before what the state is called. */
  neverApprovedPanelLead: `Nothing in this ${LESSON} has ever been approved, so employees cannot see any of it yet.`,
  neverApprovedPanelRest: `Approving it once puts the whole ${LESSON} on their phones. After that, only the parts you change go back into this list.`,

  // ── item row ──────────────────────────────────────────────────────────────
  /**
   * Wire `change_kind` → the word the reader sees. NOTES §11: "the wire names the
   * transition, the chip names what the reader is looking at." Do not "fix" the
   * mismatch in either direction.
   */
  changeKind: {
    created: 'Added',
    updated: 'Changed',
    deleted: 'Removed',
  } as const,
  /** AT-26c ruling 3. The word "Last" is the entire fix. */
  lastEditedBy: (who: string, when: string) => `Last edited by ${who} · ${when}`,
  breadcrumb: (drillTitle: string) => `${QUIZ_TERMS.drill.plural} ▸ ${drillTitle}`,
  itemPosition: (kind: string, n: number, label: string) => `${kind} ${n} — “${label}”`,

  // ── the diff ──────────────────────────────────────────────────────────────
  /** Ruling 1. Pin these two strings; they are not interchangeable with Before/After. */
  approvedColumn: 'On phones now',
  draftColumn: 'Draft',
  /** Ruling 2. */
  differs: '· differs',
  /**
   * NOTES §5: the rogue edit (AT-13) is a ONE-CHARACTER diff and must not read as
   * a small one. Ordering does that work — these fields sort first — and this tag
   * only explains why the row is on top. A *category* marker, deliberately not an
   * alarm: the editor may well be correcting a genuine error.
   */
  answerKeyTag: 'answer key',
  /** §10.7: a sentence asserts knowledge; a dash asserts ignorance. */
  emptyApprovedValue: 'nothing — this field was empty',
  /** `change_kind: "created"` — every `approved_value` is null (ADR-B §3.2 ruling 3). */
  notOnPhones: '— (not on phones)',
  showAllLines: (n: number) => `Show all ${n} lines`,
  showFewerLines: 'Show fewer lines',
  showAllItems: (n: number) => `Show all ${n}`,
  /** "+ 6 more Questions and 1 Reading, all new." is assembled from the payload. */
  moreItems: (n: number) => `+ ${n} more`,
  alreadyReviewed: (n: number) => `${changeCount(n)} you already reviewed.`,

  // ── removal panel (AC6 / D5) — a DELETED item, which is already gone ───────
  removedHeading: 'Removed from phones — already gone',
  restore: (term: string) => `↺ Restore this ${term}`,
  restoreNote: 'Restoring puts it back for employees straight away.',

  /**
   * AT-26b / NOTES §10.12 — a removed rehearsal CRITERION, which is NOT a
   * deletion and must not get the panel above.
   *
   * `n` is the count of criteria in the APPROVED value: what learners are graded
   * on right now. That is a render of one side, not a comparison of two — the
   * decision that the field changed at all is the server's (`fields[].changed`).
   * The sentence is true in both directions, which matters, because the branch
   * that selects it is `change_kind` and the entity type, NEVER "the list got
   * shorter".
   */
  criteriaStillGradedLead: (n: number) => `Employees are still graded on all ${n}.`,
  criteriaStillGradedRest: `Taking a step out of a ${QUIZ_TERMS.rehearsal.singular} is an edit, not a deletion — it takes effect when you approve this ${LESSON}, not before.`,

  // ── bulk bar ──────────────────────────────────────────────────────────────
  bulkSelected: (lessons: number, changes: number) =>
    `${termCount(lessons, 'lesson')} selected · ${changeCount(changes)}`,
  clear: 'Clear',
  /** The label carries the count AND the consequence. */
  bulkApprove: (n: number) => `Approve ${termCount(n, 'lesson')} → phones`,

  // ── AT-28 / AC4 — the store manager ───────────────────────────────────────
  /**
   * NOTES §4: the bulk bar is still rendered, permanently, with ONE disabled pill
   * in the primary slot — "a suppressed action's pill has to occupy the button's
   * slot at the same geometry — that is what makes the absence of a control read
   * as a decision rather than as a missing feature." Not eight dead checkboxes.
   */
  approveGateNote:
    "You can edit content and see what's waiting. Approving is done by an admin or a corporate manager.",

  // ── empty (state C) — a MEASUREMENT, and it must read as one ──────────────
  emptyTitle: 'Everything is approved.',
  emptyBody: (lessons: number, tenant: string) =>
    `All ${termCount(lessons, 'lesson')} at ${tenant} match what employees see on their phones.`,
  /** Naming the last approval is what makes the zero bankable. */
  emptyLastApproval: (lessonTitle: string, who: string, when: string) =>
    `The last approval was ${lessonTitle}, by ${who}, on ${when}.`,
  emptyAction: 'Edit content',

  // ── error (state D) — asserts IGNORANCE, never a zero ─────────────────────
  errorTitle: "Couldn't load what's waiting for approval.",
  /** The second sentence is the one that does the work. */
  errorBody: `This is a loading problem, not an answer — there may well be ${LESSONS} waiting. Nothing has changed on employees' phones.`,
  errorRetry: '↻ Retry',

  // ── no tenant (state G) ───────────────────────────────────────────────────
  noTenant: 'No tenant selected. Return to a scorecard page and pick a tenant first.',

  // ── result strip (state H) — PERSISTENT, never a toast ────────────────────
  /**
   * feat-199's rule: "a toast is a caption with a timer; it vanishes, and the
   * hazard is precisely that she is not looking when it does." And it NAMES the
   * lessons — "a generic confirmation makes a mis-click legible as *something
   * happened*; naming them makes it legible as *wrong*, and only the second is a
   * control."
   */
  approvedStrip: (n: number, at: string) =>
    `Approved ${termCount(n, 'lesson')} at ${at} — they're on employees' phones now.`,
  /** Spec D8: there is no un-approve, and the strip says so rather than implying one. */
  noUndoNote:
    'To put something back the way it was, open History in the editor and revert it — approving cannot be undone on its own.',

  // ── 409 (state I) — the guard WORKED. Not an error. ───────────────────────
  /**
   * NOTES §6a: copy leads with what did NOT happen, and names the change relative
   * to the reader's own arrival — "after you opened this page" is the frame that
   * makes the situation legible; a wall-clock time is a fact they must do
   * arithmetic on.
   */
  conflictStripTitle: (n: number) =>
    `${termCount(n, 'lesson')} ${n === 1 ? 'was' : 'were'} not approved — ${
      n === 1 ? 'it' : 'they'
    } changed while you were reading.`,
  conflictStripBody: (who: string) =>
    `${who} made another change after you opened this page.`,
  conflictStripNothingApproved: 'Nothing in it was approved',
  conflictStripRest:
    ", so employees still see the version you approved last. It's below with the newest changes included.",
  conflictPanelLead: (now: number, before: number) => `${changeCount(now)} now, not ${before}.`,
  conflictPanelRest: 'below is the one you haven’t seen. Approving now approves all of them.',
  /**
   * AT-27b. THE badge. Without it the approver re-reads four diffs looking for
   * the one that moved, fails to find it, and clicks through — and a 409 retried
   * blind has exactly the same outcome as no 409 at all. Server-supplied
   * (`is_new_since_review`); never derived here.
   */
  newSinceOpened: 'New since you opened this page',
  /** Partial success (AT-27c): a lesson whose approve failed for an ordinary reason. */
  failedStripTitle: (n: number) =>
    `${termCount(n, 'lesson')} could not be approved just now.`,
  failedStripBody:
    "Nothing on employees' phones changed for these. Try again, or reload the page.",

  // ── select-all confirm (state K) — AT-27d ─────────────────────────────────
  /**
   * NOTES §6b: its job is NOT to ask "are you sure". A bare count adds friction
   * without information, which is this feature's stated failure mode merely
   * relocated. Its job is to name what a select-all selection HIDES — the reader
   * chose every lesson with one click, so they have not read those diffs.
   */
  confirmTitle: (n: number) => `Approve all ${termCount(n, 'lesson')}?`,
  confirmLedeStrong: 'every waiting change',
  confirmLede: (changes: number) =>
    ` on employees' phones straight away — ${changeCount(changes)} in all.`,
  confirmLedePrefix: 'This puts ',
  /** Subset 1: whole lessons reaching employees for the first time. */
  confirmNeverApproved: (n: number) =>
    `${n} of these ${n === 1 ? 'has' : 'have'} never been approved`,
  confirmNeverApprovedRest: (n: number) =>
    ` — whole ${n === 1 ? LESSON : LESSONS} employees have never seen, going live for the first time.`,
  /** Subset 2: the rogue vector (AT-13), a one-character diff that looks like nothing. */
  confirmAnswerKeys: (n: number) =>
    `${n} change${n === 1 ? 's' : ''} a correct answer.`,
  confirmAnswerKeysRest: ' Those are one-character edits and are easy to scroll past.',
  /**
   * AT-27e, the truncated-ZERO case — and it is the one that matters, because it
   * is the one where saying nothing is a lie.
   *
   * `items` truncates at `item_limit` (ADR-B §3.2, default 10), so a count of
   * answer-key changes over the RENDERED items is a lower bound. "At least N"
   * covers that honestly whenever N > 0. It does nothing at all when N is 0: the
   * line is simply not rendered, and the one dialog written to name the rogue
   * vector goes silent about it — which a reader takes as "there are none".
   * Absence of a marker is a claim.
   *
   * So the line is driven off `items_truncated`, not off the count. This is the
   * ordinary case rather than an exotic one: a fresh tenant's first approval
   * makes every item "created", so most lessons truncate.
   */
  confirmAnswerKeysMaybe:
    'Some of these may change a correct answer',
  confirmAnswerKeysMaybeRest:
    ' — there are more changes here than this list can check.',
  confirmGroupNeverApproved: 'Never approved',
  confirmGroupChanged: 'Changed',
  confirmNewBadge: 'New',
  confirmCancel: 'Cancel',
  confirmApprove: (n: number) => `Approve all ${n}`,

  // ── the flag is off for this tenant (AT-29, reached only by direct URL) ───
  /**
   * AT-29's surface is ABSENT, not empty — no badge, no entry point, no
   * per-lesson state. But the route still exists and can be reached by a pasted
   * link, so it needs one honest sentence. It must NOT be the empty state:
   * "Everything is approved" would be a lie about a tenant where nothing is
   * gated at all, and it is the same empty-vs-error confusion one category over.
   */
  notEnabledTitle: 'Approvals are not turned on here.',
  notEnabledBody:
    "Changes at this tenant go to employees' phones as soon as they are saved. There is nothing waiting for approval because nothing is held back.",

  // ── loading (state B) ─────────────────────────────────────────────────────
  loading: 'Loading approvals',
} as const;

/**
 * The editor-side strings (`editor-integration.html`). Kept in the same module
 * because the two surfaces must not describe one state two ways, which is the
 * failure `quizTerminology.ts` was created to prevent one level down.
 */
export const APPROVAL_EDITOR_COPY = {
  /** The header link, alongside `Translations →` and `Review mode →`. */
  queueLink: 'Approvals →',
  /** AT-25: the header badge. Zero pending renders NO badge — never a "0". */
  headerBadge: (lessons: number, changes: number) =>
    `${termCount(lessons, 'lesson')} waiting · ${changeCount(changes)}`,
  /**
   * The tree chip. NOTES §3a: the VISIBLE content is the glyph alone (22.00px);
   * the sentence rides `title` + `aria-label`, because the full label measured
   * 60.08px — 42% of the 157.09px title budget — and truncated a 15-character
   * lesson name that fitted before. A test asserting visible text will not see
   * the sentence: pin the `aria-label`.
   */
  treeChipNeverApproved: '⚠',
  treeChipNeverApprovedLabel: `Never approved — employees cannot see this ${LESSON} yet`,
  treeChipPendingLabel: (n: number) =>
    `${changeCount(n)} waiting for approval — employees still see the approved version`,

  /** The per-field divergence line, under the input (§10.3, §10.4). */
  onPhonesNow: 'On phones now:',
  /** §10.10: absence of a marker is a CLAIM, so a failed fetch must say so. */
  stateFetchFailed:
    "Couldn't check what's approved. This panel can't show what employees see right now — your edits are still saving normally.",
  stateFetchRetry: 'Try again',

  panelApprovedBy: (who: string, when: string) => `Approved by ${who} on ${when}`,
  panelPending: (n: number) =>
    `${changeCount(n)} waiting for approval — employees still see the approved version.`,
  panelNeverApproved: `Employees can’t see this ${LESSON} yet — it has never been approved.`,
} as const;
